import { colors as CropCircleColors } from '../components/crop-circle/tokens/colors';

/* ────────────────────────────────────────────────────────────────────────────
 * Role tokens: the theming vocabulary, as static per-mode hex values.
 *
 * The `Colors` map in `colors.ts` is hue-named (`white`, `black`, `grayText`).
 * That is fine for a single theme but cannot flip: `Colors.white` is a surface
 * in some places and text-on-orange in others, and those two must move in
 * opposite directions under dark mode. Role tokens name what a colour is
 * *for*, so a mode switch is a lookup rather than a judgement call.
 *
 * This file is deliberately platform-neutral and must never grow a `.web.ts`
 * sibling: it is the import target for both `roleTokens.web.ts` (which turns
 * these into CSS variables) and `themeCss.ts` (which needs the raw hex to
 * generate the CSS variable declarations). A platform sibling would make
 * Metro resolve those imports to the web override and reintroduce the
 * self-import cycle that broke the first cut of this feature (see
 * `roleTokens.web.ts` for the mechanics).
 *
 * Light values are taken from what production already renders today, so the
 * call-site rename is a genuine no-op rather than a redesign. Dark values are
 * the neutral ramp validated in Gripp OS on 2026-07-27: hue-free (R=G=B) and
 * anchored on Crop Circle neutrals, all eight of which are byte-identical
 * between the two codebases.
 *
 * Why hue-free: the first dark pass built surfaces from the `slate` family
 * (#1A1D24, #262A33). Slate reads fine as a light-mode accent, but at
 * full-surface scale its blue cast made the whole app look navy rather than
 * dark. Brand orange and the semantics keep their hue; nothing else does.
 * ──────────────────────────────────────────────────────────────────────── */

export type ThemeMode = 'light' | 'dark';

export type RoleToken =
  // surfaces, lightest to most raised
  | 'canvas'
  | 'canvasRecessed'
  | 'surface'
  | 'surfaceRaised'
  | 'surfaceHover'
  | 'surfaceSelected'
  | 'surfaceActive'
  | 'inputBackground'
  // inverse fills (dark-on-light in light mode, light-on-dark in dark mode)
  | 'surfaceInverse'
  | 'surfaceInverseHover'
  | 'surfaceInverseSoft'
  | 'surfaceInverseSoftHover'
  | 'surfaceInverseSoftActive'
  | 'surfaceDestructiveSoft'
  // borders, weakest to strongest
  | 'borderSubtle'
  | 'borderSection'
  | 'borderControl'
  | 'borderStrong'
  | 'borderEmphasis'
  // muted / disabled fills and glyphs
  | 'fillMuted'
  | 'fillDisabled'
  // text
  | 'textStrong'
  | 'textPrimary'
  | 'textSecondary'
  | 'textSoft'
  | 'textDisabled'
  | 'textOnInverse'
  | 'textOnBrand'
  | 'brandOnSurface'
  // alpha composites — pre-resolved per mode because a CSS variable cannot
  // take a concatenated alpha suffix on the web build
  | 'surfaceGlass'
  | 'brandTint'
  | 'scrim'
  | 'veil'
  | 'inverseTint'
  | 'inverseDim'
  | 'destructiveTint';

export const roleColorValues: Record<ThemeMode, Record<RoleToken, string>> = {
  light: {
    canvas: CropCircleColors.neutral[100], // #FCFCFC
    canvasRecessed: CropCircleColors.neutral[200], // #F3F3F3 — app frame, nav rail
    surface: CropCircleColors.brand.fullWhite, // #FFFFFF
    surfaceRaised: CropCircleColors.slate[100], // #ECEEF1 — table/panel headers
    surfaceHover: CropCircleColors.neutral[200], // #F3F3F3
    surfaceSelected: CropCircleColors.slate[100], // #ECEEF1
    surfaceActive: CropCircleColors.neutral[300], // #D8D8D8
    inputBackground: CropCircleColors.neutral[200], // #F3F3F3

    surfaceInverse: CropCircleColors.neutral[800], // #020202 — primary buttons, active pills
    surfaceInverseHover: CropCircleColors.neutral[600], // #2F2F2F — hover on inverse fills
    surfaceInverseSoft: CropCircleColors.slate[100], // #ECEEF1 — soft gray pills, CardButton
    surfaceInverseSoftHover: CropCircleColors.slate[200], // #C8CDD5
    surfaceInverseSoftActive: CropCircleColors.slate[300], // #A1A8B5 — selected soft pill
    surfaceDestructiveSoft: '#FEF2EF',

    borderSubtle: CropCircleColors.neutral[200], // #F3F3F3 — row dividers
    borderSection: CropCircleColors.slate[100], // #ECEEF1 — panel/card edges
    borderControl: CropCircleColors.neutral[300], // #D8D8D8 — inputs, outlines
    borderStrong: CropCircleColors.neutral[400], // #B4B4B4 — emphasized rules
    // The heaviest outline: input and dropdown edges that were hardcoded
    // #6C6C6C before the migration. Exists so those sites can stop borrowing
    // `textSecondary`, whose dark value (#A3A3A3) is tuned for TEXT contrast
    // and paints a bold outline when used on a 1px border. Light is
    // byte-identical to what shipped; only dark differs.
    borderEmphasis: CropCircleColors.system.textSecondary, // #6C6C6C

    fillMuted: CropCircleColors.system.muted, // #9FA6B2 — quiet icons, inactive fills
    fillDisabled: CropCircleColors.system.disabled, // #CCCCCC — placeholders, disabled controls

    textStrong: CropCircleColors.neutral[800], // #020202
    textPrimary: CropCircleColors.system.textPrimary, // #2F2F2F
    textSecondary: CropCircleColors.system.textSecondary, // #6C6C6C
    textSoft: CropCircleColors.slate[400], // #7A8394 — timestamps, placeholders
    textDisabled: CropCircleColors.neutral[400], // #B4B4B4 — inactive nav items, disabled labels
    textOnInverse: CropCircleColors.neutral[100], // #FCFCFC
    textOnBrand: CropCircleColors.brand.fullWhite, // #FFFFFF — on brand orange
    brandOnSurface: CropCircleColors.brand.core, // #FF5505

    // Translucent card fill sitting over the dashboard backdrop (design:
    // bg-card/85). `surface` at 85%, written out because the web build turns
    // these into CSS variables and cannot append an alpha suffix to one.
    surfaceGlass: `${CropCircleColors.brand.fullWhite}D9`, // surface @ 85%
    brandTint: `${CropCircleColors.brand.core}1A`,
    scrim: `${CropCircleColors.neutral[800]}4D`,
    veil: `${CropCircleColors.brand.white}E6`,
    inverseTint: `${CropCircleColors.neutral[800]}1A`,
    inverseDim: `${CropCircleColors.neutral[800]}BF`,
    destructiveTint: `${CropCircleColors.semantic.error}59`,
  },

  dark: {
    // Elevation ladder. Every value below is hue-free by design.
    canvas: '#141414', // page + nav chrome
    canvasRecessed: '#141414', // frame merges with canvas in dark; panels rise above
    surface: '#1C1C1C', // cards, list rows, panels
    surfaceRaised: '#232323', // popovers, headers, subtle fills
    surfaceHover: '#262626',
    surfaceSelected: '#2C2C2C',
    surfaceActive: '#333333',
    inputBackground: '#262626',

    surfaceInverse: CropCircleColors.neutral[100], // #FCFCFC — white pill on dark
    surfaceInverseHover: '#333333', // hover pill on dark chrome
    surfaceInverseSoft: '#292929',
    surfaceInverseSoftHover: '#343434',
    surfaceInverseSoftActive: '#3E3E3E',
    surfaceDestructiveSoft: '#2A1512', // warm, intentionally hue-carrying

    borderSubtle: '#242424',
    borderSection: '#303030',
    borderControl: CropCircleColors.neutral[600], // #2F2F2F
    borderStrong: '#454545', // clear step above borderControl, still recessive
    // Deliberately equal to borderStrong: the border ramp compresses in dark,
    // because a heavier outline on a dark fill reads as a bright line rather
    // than a firmer edge. The light-mode step from borderStrong has no dark
    // counterpart worth spending contrast on.
    borderEmphasis: '#454545',

    fillMuted: '#707070', // hue-free twin of the slate muted fill
    fillDisabled: '#4A4A4A', // reads as inert on #262626 inputs without vanishing

    textStrong: CropCircleColors.brand.fullWhite, // #FFFFFF
    textPrimary: CropCircleColors.neutral[100], // #FCFCFC
    // Tuned to mirror light-mode contrast rather than invert it literally:
    // ~6.8:1 and ~4.9:1 on the #1C1C1C surface.
    textSecondary: '#A3A3A3',
    textSoft: '#8A8A8A',
    textDisabled: '#757575', // readable-muted on the dark rail without competing
    textOnInverse: CropCircleColors.neutral[700], // #1A1A1A — text on the white pill
    textOnBrand: CropCircleColors.brand.fullWhite, // #FFFFFF — constant across modes
    brandOnSurface: CropCircleColors.brand.core, // #FF5505 — constant across modes

    surfaceGlass: '#1C1C1CD9', // `surface` #1C1C1C at 85%
    brandTint: `${CropCircleColors.brand.core}26`, // lifted so it reads on dark
    scrim: '#00000099', // deeper than light mode; the page beneath is already dark
    veil: '#141414E6', // dark veil, not the light-mode white one
    inverseTint: `${CropCircleColors.neutral[100]}1A`, // inverts: light tint on dark
    inverseDim: `${CropCircleColors.neutral[100]}BF`,
    destructiveTint: `${CropCircleColors.semantic.error}59`,
  },
};

/**
 * `surfaceRaised` -> `--surface-raised`. Single source for the token-name to
 * CSS-variable-name convention, shared by the web override (`roleTokens.web.ts`)
 * and the CSS generator (`themeCss.ts`).
 */
export const cssVarName = (token: RoleToken): string =>
  `--${token.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`)}`;
